import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type { ComponentProps } from "react";
import { DealConstructionScheduleVendorCard } from "~/components/common/deals/show/construction_card/DealConstructionScheduleVendorCard";
import { DealCard } from "~/components/common/deals/show/deal_card/DealCard";
import { DealInspectionCandidateDateCard } from "~/components/common/deals/show/inspection_candidate_date_card/DealInspectionCandidateDateCard";
import { DealRepairCard } from "~/components/common/deals/show/repair_card/DealRepairCard";
import { DealNoteWithinVendorCard } from "~/components/common/deals/show/vendor_card/DealNoteWithinVendorCard";
import { DealOriginVendorCard } from "~/components/common/deals/show/vendor_card/DealOriginVendorCard";
import { DealSubVendorCard } from "~/components/common/deals/show/vendor_card/DealSubVendorCard";
import { DealVendorNoteCard } from "~/components/common/deals/show/vendor_card/DealVendorNoteCard";
import { DealVisitDateVendorCard } from "~/components/common/deals/show/visit_date_card/DealVisitDateVendorCard";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loader from "~/components/common/parts/loading/Loading";
import { VendorSessionProvider } from "~/context/VendorSession";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH as DEAL_PATH } from "~/pages/vendors/deals/[id]";

export function Page() {
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const { data: deal, isLoading, mutate: mutateDeal } = useDeals().show(id);
	const { data: session } = useSessions().index();

	const handleMutateDeal = async (): Promise<void> => {
		await mutateDeal();
	};

	if (isLoading || deal === null || session === null) {
		return <Loader isLoading />;
	}

	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: DEAL_PATH(deal.id), label: "案件詳細" },
		{ label: "案件内容" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];

	return (
		<VendorSessionProvider session={session}>
			<VendorLayoutV2>
				<VendorDealLayout deal={deal} items={items} noBackground>
					<DealVendorNoteCard deal={deal} handleMutateDeal={handleMutateDeal} />
					<DealNoteWithinVendorCard
						deal={deal}
						handleMutate={handleMutateDeal}
					/>
					<DealCard deal={deal} isVendor vendorSession={session} />
					<DealOriginVendorCard deal={deal} />
					{deal.dealType === "repair" && (
						<DealRepairCard companyType="vendor" deal={deal} />
					)}
					<DealSubVendorCard deal={deal} />
					<DealVisitDateVendorCard />
					<DealInspectionCandidateDateCard
						deal={deal}
						handleMutate={handleMutateDeal}
						isVendor
					/>
					<DealConstructionScheduleVendorCard deal={deal} />
				</VendorDealLayout>
			</VendorLayoutV2>
		</VendorSessionProvider>
	);
}
